import { Link as MuiLink, type LinkProps as MuiLinkProps } from '@mui/material';
import { Link as FplLink } from '@figma/fpl-components';
import { forwardRef } from 'react';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

export type LinkProps = MuiLinkProps;

/**
 * Weave `Link`. `fpl-migration` boundary: flag-off renders the legacy MUI `Link` unchanged;
 * flag-on renders the high-level FPL `Link`.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled === true) {
    const { href, children, target, onClick, 'aria-label': ariaLabel, 'aria-current': ariaCurrent, id, title } = props;

    return (
      <FplLink
        ref={ref}
        href={href ?? ''}
        newTab={target === '_blank'}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-current={ariaCurrent}
        id={id}
        title={title}
      >
        {children}
      </FplLink>
    );
  }

  const { color, sx, ...rest } = props;
  // This path still renders until PostHog's flags arrive, even with `fpl-migration` on, and callers such
  // as Model Management already pass FPL tokens. MUI derives the underline from `color` with `alpha()`,
  // which throws on a CSS variable, so a token goes through `sx` with the same 40% underline instead.
  if (typeof color === 'string' && color.startsWith('var(')) {
    const tokenColor = { color, textDecorationColor: `color-mix(in srgb, ${color} 40%, transparent)` };
    return <MuiLink ref={ref} {...rest} sx={[tokenColor, ...(Array.isArray(sx) ? sx : [sx])]} />;
  }

  return <MuiLink ref={ref} {...props} />;
});
